import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { formatCurrencyByCountry } from '../lib/constants'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'
import FoundingListerBadge from '../components/FoundingListerBadge'
import ListingPlaceholder from '../components/ListingPlaceholder'
import { startDirectMessage } from '../lib/directMessage'

export default function OwnerProfile() {
const { id } = useParams()
const navigate = useNavigate()
const { user, profile: viewerProfile } = useAuth()
const [profile, setProfile] = useState(null)
const [listings, setListings] = useState([])
const [reviews, setReviews] = useState([])
const [stats, setStats] = useState({ totalHires: 0, activeListings: 0, avgResponseTime: null })
const [loading, setLoading] = useState(true)
const [dmInFlight, setDmInFlight] = useState(false)

useEffect(() => {
fetchProfile()
}, [id])

const fetchProfile = async () => {
setLoading(true)

// Get the user's profile
const { data: profileData } = await supabase
.from('public_profiles')
.select('id, full_name, avatar_url, suburb, rating, review_count, created_at, founding_lister_number, founding_lister_suburb')
.eq('id', id)
.single()

setProfile(profileData)

// Get their active listings
const { data: listingsData } = await supabase
.from('listings')
.select('*')
.eq('owner_id', id)
.eq('available', true)
.eq('status', 'active')
.is('archived_at', null)
.eq('admin_disabled', false)
.order('created_at', { ascending: false })

setListings(listingsData || [])

// Get reviews about them (as owner)
const { data: reviewsData } = await supabase
.from('reviews')
.select('*, reviewer:profiles!reviews_reviewer_id_fkey(full_name, avatar_url)')
.eq('reviewee_id', id)
.order('created_at', { ascending: false })
.limit(10)

setReviews(reviewsData || [])

// Calculate stats
const { count: completedHires } = await supabase
.from('bookings')
.select('*', { count: 'exact', head: true })
.eq('owner_id', id)
.eq('status', 'completed')

setStats({
totalHires: completedHires || 0,
activeListings: listingsData?.length || 0,
})

setLoading(false)
}

if (loading) return (
<div><Header />
<div className="spinner-wrap"><div className="spinner" /></div>
</div>
)

if (!profile) return (
<div><Header />
<div className="container section" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
<div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
<h2 style={{ marginBottom: 8 }}>Profile not found</h2>
<p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>This user doesn't exist or has been removed.</p>
<button className="btn btn-gradient" onClick={() => navigate('/')}>Back to listings</button>
</div>
</div>
)

const isOwnProfile = user?.id === id
const memberSince = profile.created_at
? new Date(profile.created_at).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })
: null
const initials = profile.full_name
? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
: '?'
const reviewCount = profile?.review_count || 0
const hasRating = reviewCount > 0 && profile?.rating != null && profile?.rating > 0
const rating = hasRating ? profile.rating.toFixed(1) : null

const handleAdminMessage = async () => {
if (!viewerProfile?.is_admin || !user || !profile?.id) return
if (dmInFlight) return
setDmInFlight(true)
try {
// Reuses an open direct-message thread to this user, or starts one
try {
const conversationId = await startDirectMessage(user.id, profile.id)
navigate(`/messages?booking=${conversationId}`)
} catch (err) {
alert('Could not start conversation: ' + err.message)
}
} finally {
setDmInFlight(false)
}
}

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 700, padding: '1.5rem 1rem' }}>
<button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: '1rem' }}>← Back</button>

{/* Profile header */}
<div className="card card-body" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
<div style={{ width: 100, height: 100, borderRadius: '50%', background: profile.avatar_url ? 'transparent' : 'rgba(45, 63, 204, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '3px solid var(--primary)', marginBottom: 12 }}>
{profile.avatar_url ? (
<img src={profile.avatar_url} alt={profile.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
) : (
<span style={{ fontSize: 36, fontWeight: 700, color: 'var(--primary)' }}>{initials}</span>
)}
</div>
<h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>
{profile.full_name || 'Anonymous'}
</h1>
{profile.founding_lister_number && (
<div style={{ marginBottom: 8 }}>
<FoundingListerBadge profile={profile} size="lg" />
</div>
)}
{profile.suburb && (
<div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 4 }}>
📍 {profile.suburb}
</div>
)}
{memberSince && (
<div style={{ fontSize: 13, color: 'var(--text-faint)' }}>
📅 Member since {memberSince}
</div>
)}
</div>

{/* Stats row */}
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
<div style={{ background: 'var(--gray-50)', borderRadius: 10, padding: '10px 6px' }}>
<div style={{ fontSize: 18, fontWeight: 700, color: hasRating ? 'var(--primary)' : 'var(--text-faint)' }}>{hasRating ? `⭐ ${rating}` : 'New'}</div>
<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hasRating ? `${reviewCount} review${reviewCount === 1 ? '' : 's'}` : 'Not yet rated'}</div>
</div>
<div style={{ background: 'var(--gray-50)', borderRadius: 10, padding: '10px 6px' }}>
<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>{stats.totalHires}</div>
<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>successful hire{stats.totalHires === 1 ? '' : 's'}</div>
</div>
<div style={{ background: 'var(--gray-50)', borderRadius: 10, padding: '10px 6px' }}>
<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>{stats.activeListings}</div>
<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>active listing{stats.activeListings === 1 ? '' : 's'}</div>
</div>
</div>

{profile.bio && (
<div style={{ background: 'var(--primary-pale)', borderRadius: 10, padding: '12px 14px', fontSize: 14, color: 'var(--navy)', lineHeight: 1.6, textAlign: 'left' }}>
{profile.bio}
</div>
)}

{isOwnProfile && (
<button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => navigate('/profile')}>
✏️ Edit my profile
</button>
)}
{!isOwnProfile && viewerProfile?.is_admin && (
<button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={handleAdminMessage} disabled={dmInFlight}>
{dmInFlight ? 'Opening…' : '💬 Message (admin)'}
</button>
)}
</div>

{/* Listings */}
{listings.length > 0 && (
<div style={{ marginBottom: '1.5rem' }}>
<h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>
{isOwnProfile ? 'My listings' : `${profile.full_name?.split(' ')[0] || 'Their'} listings`} ({listings.length})
</h2>
<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
{listings.map(listing => {
return (
<div key={listing.id} className="card card-body" style={{ cursor: 'pointer', padding: '12px' }} onClick={() => navigate(`/listing/${listing.id}`)}>
<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
<div style={{ width: 72, height: 72, borderRadius: 10, background: 'var(--primary-pale)', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
{listing.images?.[0] ? (
<img src={listing.images[0]} alt={listing.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
) : (
<ListingPlaceholder listing={listing} size={72} />
)}
</div>
<div style={{ flex: 1, minWidth: 0 }}>
<div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
{listing.title}
</div>
<div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
📍 {listing.suburb || listing.postcode}
</div>
<div style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)' }}>
{[
listing.price_per_hour && `${formatCurrencyByCountry(listing.price_per_hour, listing.country || 'Australia')}/hr`,
listing.price_per_day && `${formatCurrencyByCountry(listing.price_per_day, listing.country || 'Australia')}/day`,
listing.price_per_week && `${formatCurrencyByCountry(listing.price_per_week, listing.country || 'Australia')}/wk`,
].filter(Boolean).join(' · ')}
</div>
</div>
<div style={{ color: 'var(--text-faint)', fontSize: 18 }}>›</div>
</div>
</div>
)
})}
</div>
</div>
)}

{/* Reviews */}
<div>
<h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>
Reviews ({reviewCount})
</h2>
{reviews.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
<div style={{ fontSize: 32, marginBottom: 8 }}>⭐</div>
No reviews yet
</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
{reviews.map(review => {
const reviewerInitials = review.reviewer?.full_name
? review.reviewer.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
: '?'
return (
<div key={review.id} className="card card-body">
<div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
<div style={{ width: 36, height: 36, borderRadius: '50%', background: review.reviewer?.avatar_url ? 'transparent' : 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
{review.reviewer?.avatar_url ? (
<img src={review.reviewer.avatar_url} alt={review.reviewer.full_name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
) : (
<span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{reviewerInitials}</span>
)}
</div>
<div style={{ flex: 1 }}>
<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
<span style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>
{review.reviewer?.full_name || 'Anonymous'}
</span>
<span style={{ fontSize: 13, color: 'var(--amber)' }}>
{'⭐'.repeat(review.rating || 5)}
</span>
</div>
{review.comment && (
<p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, margin: 0 }}>
"{review.comment}"
</p>
)}
<div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>
{new Date(review.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
</div>
</div>
</div>
</div>
)
})}
</div>
)}
</div>
</div>
</div>
)
}